import { useState } from 'react'
import type { Product, ProductFamily } from '@domain/product'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { EscolherFamilia } from './EscolherFamilia'

/**
 * A qual família este produto pertence — o gatilho, no cabeçalho.
 *
 * ## De `<select>` nativo para uma tela inteira (decisão 242)
 *
 * O seletor do sistema funciona com três famílias e quebra com trinta: não dá
 * para buscar, ele não mostra quantos tamanhos cada família tem, e criar uma
 * exigia fechar o seletor e achar um link "Criar família" ao lado. Aqui o
 * gatilho mostra a escolha atual e abre `EscolherFamilia`, que busca, mostra
 * as fotos de cada família e cria a que falta sem sair dali.
 *
 * ## Ele saiu da dobra "Tamanhos" e subiu para o cabeçalho (decisão 240)
 *
 * Escolher a família é uma **decisão sobre o produto**, da mesma natureza de
 * corrigir o nome ou marcar o ★. A régua que compara os tamanhos é o
 * **resultado** dessa escolha, e continua embaixo, com o espaço de que precisa.
 */
export function SeletorDeFamilia({
  produto,
  familias,
  onTrocarFamilia,
  onCriouFamilia,
}: {
  produto: Product
  /**
   * A lista, carregada pela PÁGINA e não aqui.
   *
   * A régua de tamanhos precisa do nome da família atual, e o painel precisa
   * da lista inteira. Cada um lendo por conta daria duas consultas iguais na
   * mesma tela, com a segunda chegando um instante depois — tempo suficiente
   * para um rótulo mudar sozinho na cara de quem olha.
   */
  familias: ProductFamily[]
  /** Quem manda no produto é a tela de cima; aqui só se pede a troca. */
  onTrocarFamilia: (familyId: string | null) => void
  /**
   * Uma família NOVA existe. Só isto releva a lista.
   *
   * Trocar de família não muda o elenco de famílias, e reler a cada troca
   * custaria uma volta ao servidor para saber o que já se sabe — e, pior,
   * desfaria a troca otimista por um instante se a escrita ainda não tivesse
   * chegado. Criar é o único caso em que a lista de fato mudou.
   */
  onCriouFamilia: () => void
}) {
  const { productRepository } = useFinanceRepositories()
  const [escolhendo, setEscolhendo] = useState(false)

  const atual = familias.find((f) => f.id === produto.familyId) ?? null

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setEscolhendo(true)
        }}
        // O nome da família fica no RÓTULO, e não só num `aria-label`: o
        // cabeçalho precisa responder "a que grupo isto pertence" sem ninguém
        // tocar em nada.
        aria-label={`Família: ${atual?.name ?? 'sem família'}`}
        className="alvo max-w-[9.5rem] gap-1 rounded-full border border-[var(--border)] px-3 text-xs"
      >
        <span className="truncate">{atual?.name ?? 'Sem família'}</span>
        <span aria-hidden="true" className="text-[var(--text-faint)]">
          ⌄
        </span>
      </button>

      {escolhendo && (
        <EscolherFamilia
          atual={produto.familyId}
          familias={familias}
          onEscolher={(familyId) => {
            onTrocarFamilia(familyId)
            setEscolhendo(false)
          }}
          onCriar={(nome) => {
            void productRepository
              .createFamily(nome)
              .then((id) => {
                // Criar e entrar são o mesmo gesto: ninguém cria uma família
                // para deixar o produto de fora dela.
                onTrocarFamilia(id)
                onCriouFamilia()
                setEscolhendo(false)
              })
              .catch((erro: unknown) => {
                console.error('SeletorDeFamilia: não deu para criar', erro)
              })
          }}
          onFechar={() => {
            setEscolhendo(false)
          }}
        />
      )}
    </>
  )
}
